/**
 * Voice command parsing.
 *
 * MVP scope: explicit session addressing at the start of a transcript,
 * e.g. "main what's up" or "personal remind me tomorrow".
 *
 * This lives in the voice layer so we can grow it into a fuller command
 * parser later without burying ad-hoc routing logic inside App.tsx.
 */

import type { GatewayAgent, SessionRow } from '../types/sessions';
import { SPEED_STEPS } from '../utils/constants';

export type VoiceAddressSource =
  | 'agent.id'
  | 'agent.name'
  | 'agent.identity.name'
  | 'session.spokenAlias'
  | 'session.label'
  | 'session.displayName'
  | 'session.derivedTitle';

export interface VoiceAddressTarget {
  sessionKey: string;
  alias: string;
  source: VoiceAddressSource;
  priority: number;
}

export type ParsedVoiceCommand =
  | {
      kind: 'none';
      originalText: string;
      remainingText: string;
      targetSessionKey: string;
      shouldSwitchSession: false;
    }
  | {
      kind: 'address';
      originalText: string;
      remainingText: string;
      targetSessionKey: string;
      shouldSwitchSession: boolean;
      matchedAlias: string;
      matchedBy: VoiceAddressSource;
    }
  | {
      kind: 'ambiguous';
      originalText: string;
      remainingText: string;
      targetSessionKey: string;
      shouldSwitchSession: false;
      matchedAlias: string;
      candidateSessionKeys: string[];
    };

export type VoiceControlCommandId =
  | 'mute_mic'
  | 'unmute_mic'
  | 'mute_speaker'
  | 'unmute_speaker'
  | 'faster'
  | 'slower';

export interface ParsedVoiceControlCommand {
  command: VoiceControlCommandId;
  originalText: string;
  normalizedText: string;
  matchedPhrase: string;
}

interface ParseVoiceCommandOptions {
  transcript: string;
  activeSessionKey: string;
  agents: GatewayAgent[];
  sessions: SessionRow[];
}

interface VoiceCommandContext {
  activeSessionKey: string;
  agents: GatewayAgent[];
  sessions: SessionRow[];
}

interface VoiceAddressEntry {
  alias: string;
  priority: number;
  targets: VoiceAddressTarget[];
}

export type VoiceTurnRoute =
  | {
      kind: 'none';
      targetSessionKey: string;
    }
  | {
      kind: 'address';
      targetSessionKey: string;
      matchedAlias: string;
      matchedBy: VoiceAddressSource;
    }
  | {
      kind: 'ambiguous';
      targetSessionKey: string;
      matchedAlias: string;
      candidateSessionKeys: string[];
    };

export interface VoiceTurnSegmentDecision {
  action: 'hold' | 'emit' | 'switch_only';
  text: string;
  route: VoiceTurnRoute;
  shouldSwitchSession: boolean;
}

export type RoutedVoiceTurnSegment =
  | {
      kind: 'empty';
    }
  | {
      kind: 'decision';
      decision: VoiceTurnSegmentDecision;
    };

export type ResolvedVoiceTurnCommand = VoiceTurnRoute & {
  originalText: string;
  remainingText: string;
  shouldSwitchSession: boolean;
};

const MAIN_SESSION_SUFFIX = ':main';
const MAX_DERIVED_TITLE_ALIAS_LENGTH = 40;
const MAX_DERIVED_TITLE_ALIAS_WORDS = 4;

function normalizeAlias(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^\p{L}\p{N} ]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildAliasMatcher(alias: string): RegExp {
  const parts = alias.split(' ').filter(Boolean).map(escapeRegex);
  const joined = parts.join('[^\\p{L}\\p{N}]+');
  return new RegExp(`^\\s*${joined}(?=$|[^\\p{L}\\p{N}])`, 'iu');
}

function isMainSessionKey(sessionKey: string): boolean {
  return /^agent:[^:]+:main$/u.test(sessionKey);
}

function isSubagentSessionKey(sessionKey: string): boolean {
  return sessionKey.includes(':subagent:');
}

function parseAgentIdFromSessionKey(sessionKey: string): string | null {
  const parts = sessionKey.split(':');
  if (parts.length < 2 || parts[0] !== 'agent') return null;
  return parts[1] ?? null;
}

function compareSessionsByRecency(a: SessionRow, b: SessionRow): number {
  return (b.updatedAt ?? 0) - (a.updatedAt ?? 0);
}

function resolveAgentVoiceSessionKey(
  agentId: string,
  sessions: SessionRow[],
  activeSessionKey?: string,
): string | null {
  const mainSessionKey = `agent:${agentId}${MAIN_SESSION_SUFFIX}`;
  if (sessions.some((session) => session.key === mainSessionKey)) {
    return mainSessionKey;
  }

  if (
    activeSessionKey
    && !isSubagentSessionKey(activeSessionKey)
    && parseAgentIdFromSessionKey(activeSessionKey) === agentId
    && sessions.some((session) => session.key === activeSessionKey)
  ) {
    return activeSessionKey;
  }

  return [...sessions]
    .filter((session) => !isSubagentSessionKey(session.key))
    .filter((session) => parseAgentIdFromSessionKey(session.key) === agentId)
    .sort(compareSessionsByRecency)[0]?.key ?? null;
}

function isUsableDerivedTitle(title: string): boolean {
  const alias = normalizeAlias(title);
  if (!alias) return false;
  const words = alias.split(' ').filter(Boolean);
  return alias.length <= MAX_DERIVED_TITLE_ALIAS_LENGTH && words.length <= MAX_DERIVED_TITLE_ALIAS_WORDS;
}

/**
 * STT phonetic alias map.
 *
 * Whisper and other STT engines frequently misrecognize short agent names
 * as common words. This map provides known misrecognitions so the voice
 * command parser can still match them.
 *
 * Keys are the normalized canonical alias; values are arrays of known
 * STT misrecognitions (already normalized).
 */
const STT_PHONETIC_ALIASES: Record<string, string[]> = {
  main: ['maine', 'mane', 'mame', 'mayne'],
  personal: ['personnel'],
};

const VOICE_LOCK_SWITCH_ONLY_ALIAS_AGENT_IDS = new Map<string, string>([
  ['main', 'main'],
  ...(STT_PHONETIC_ALIASES.main ?? []).map((alias) => [alias, 'main'] as const),
  ['personal', 'personal'],
  ...(STT_PHONETIC_ALIASES.personal ?? []).map((alias) => [alias, 'personal'] as const),
]);

const VOICE_CONTROL_ALIASES: Record<string, VoiceControlCommandId> = {
  mute: 'mute_mic',
  'mute mic': 'mute_mic',
  'mute mike': 'mute_mic',
  'mute microphone': 'mute_mic',
  listen: 'unmute_mic',
  'unmute mic': 'unmute_mic',
  'unmute mike': 'unmute_mic',
  'unmute microphone': 'unmute_mic',
  silence: 'mute_speaker',
  'mute speaker': 'mute_speaker',
  'mute speakers': 'mute_speaker',
  speak: 'unmute_speaker',
  'unmute speaker': 'unmute_speaker',
  'unmute speakers': 'unmute_speaker',
  faster: 'faster',
  slower: 'slower',
};

function aliasVariants(raw: string): string[] {
  const normalized = normalizeAlias(raw);
  if (!normalized || normalized.length < 2) return [];

  const variants = new Set<string>([normalized]);
  if (normalized.endsWith(' session')) {
    const withoutSuffix = normalized.slice(0, -' session'.length).trim();
    if (withoutSuffix.length >= 2) {
      variants.add(withoutSuffix);
    }
  }

  // Add known STT misrecognitions
  const phoneticAliases = STT_PHONETIC_ALIASES[normalized];
  if (phoneticAliases) {
    for (const alias of phoneticAliases) {
      variants.add(alias);
    }
  }

  return [...variants];
}

function addTarget(
  entries: Map<string, VoiceAddressEntry>,
  sessionKey: string,
  rawAlias: string | undefined,
  source: VoiceAddressSource,
  priority: number,
): void {
  if (!rawAlias) return;

  for (const alias of aliasVariants(rawAlias)) {
    const next: VoiceAddressTarget = { sessionKey, alias, source, priority };
    const existing = entries.get(alias);

    if (!existing || priority > existing.priority) {
      entries.set(alias, { alias, priority, targets: [next] });
      continue;
    }

    if (priority < existing.priority) {
      continue;
    }

    if (!existing.targets.some((target) => target.sessionKey === sessionKey)) {
      existing.targets.push(next);
    }
  }
}

function addTargets(
  entries: Map<string, VoiceAddressEntry>,
  sessionKey: string,
  rawAliases: string[] | undefined,
  source: VoiceAddressSource,
  priority: number,
): void {
  if (!rawAliases || rawAliases.length === 0) return;

  for (const alias of rawAliases) {
    addTarget(entries, sessionKey, alias, source, priority);
  }
}

function compareAliasEntries(a: Pick<VoiceAddressEntry, 'alias' | 'priority'>, b: Pick<VoiceAddressEntry, 'alias' | 'priority'>): number {
  const wordDiff = b.alias.split(' ').length - a.alias.split(' ').length;
  if (wordDiff !== 0) return wordDiff;

  const lengthDiff = b.alias.length - a.alias.length;
  if (lengthDiff !== 0) return lengthDiff;

  const priorityDiff = b.priority - a.priority;
  if (priorityDiff !== 0) return priorityDiff;

  return a.alias.localeCompare(b.alias);
}

function buildVoiceAddressEntries(params: {
  agents: GatewayAgent[];
  sessions: SessionRow[];
  activeSessionKey?: string;
}): VoiceAddressEntry[] {
  const { agents, sessions, activeSessionKey } = params;
  const entries = new Map<string, VoiceAddressEntry>();
  const sessionMap = new Map(sessions.map((session) => [session.key, session]));

  for (const agent of agents) {
    const mainSessionKey = resolveAgentVoiceSessionKey(agent.id, sessions, activeSessionKey);
    if (!mainSessionKey) continue;

    addTarget(entries, mainSessionKey, agent.id, 'agent.id', 100);
    addTarget(entries, mainSessionKey, agent.name, 'agent.name', 96);
    addTarget(entries, mainSessionKey, agent.identity?.name, 'agent.identity.name', 96);

    const mainSession = sessionMap.get(mainSessionKey);
    if (!mainSession) continue;

    addTargets(entries, mainSession.key, mainSession.spokenAliases, 'session.spokenAlias', 98);
    addTarget(entries, mainSession.key, mainSession.label, 'session.label', 92);
    addTarget(entries, mainSession.key, mainSession.displayName, 'session.displayName', 90);
    if (mainSession.derivedTitle && isUsableDerivedTitle(mainSession.derivedTitle)) {
      addTarget(entries, mainSession.key, mainSession.derivedTitle, 'session.derivedTitle', 84);
    }
  }

  for (const session of sessions) {
    if (isSubagentSessionKey(session.key)) continue;

    const isMainSession = isMainSessionKey(session.key);
    addTargets(entries, session.key, session.spokenAliases, 'session.spokenAlias', isMainSession ? 98 : 94);
    addTarget(entries, session.key, session.label, 'session.label', isMainSession ? 88 : 80);
    addTarget(entries, session.key, session.displayName, 'session.displayName', isMainSession ? 86 : 78);

    if (session.derivedTitle && isUsableDerivedTitle(session.derivedTitle)) {
      addTarget(entries, session.key, session.derivedTitle, 'session.derivedTitle', isMainSession ? 82 : 74);
    }
  }

  return [...entries.values()].sort(compareAliasEntries);
}

function parseVoiceCommandWithEntries(options: {
  transcript: string;
  activeSessionKey: string;
  entries: VoiceAddressEntry[];
}): ParsedVoiceCommand {
  const { transcript, activeSessionKey, entries } = options;
  const originalText = transcript.trim();

  if (!originalText) {
    return {
      kind: 'none',
      originalText,
      remainingText: '',
      targetSessionKey: activeSessionKey,
      shouldSwitchSession: false,
    };
  }

  for (const entry of entries) {
    const matcher = buildAliasMatcher(entry.alias);
    const match = matcher.exec(transcript);
    if (!match) continue;

    if (entry.targets.length > 1) {
      return {
        kind: 'ambiguous',
        originalText,
        remainingText: originalText,
        targetSessionKey: activeSessionKey,
        shouldSwitchSession: false,
        matchedAlias: entry.alias,
        candidateSessionKeys: [...entry.targets]
          .map((target) => target.sessionKey)
          .sort(),
      };
    }

    const target = entry.targets[0]!;
    const remainingText = transcript
      .slice(match[0].length)
      .replace(/^[^\p{L}\p{N}]+/u, '')
      .trim();

    return {
      kind: 'address',
      originalText,
      remainingText,
      targetSessionKey: target.sessionKey,
      shouldSwitchSession: target.sessionKey !== activeSessionKey,
      matchedAlias: target.alias,
      matchedBy: target.source,
    };
  }

  return {
    kind: 'none',
    originalText,
    remainingText: originalText,
    targetSessionKey: activeSessionKey,
    shouldSwitchSession: false,
  };
}

function joinTranscriptFragments(...parts: Array<string | null | undefined>): string {
  return parts
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(' ')
    .trim();
}

function hasLongerAliasPrefix(
  transcript: string,
  targetSessionKey: string,
  entries: VoiceAddressEntry[],
): boolean {
  const normalized = normalizeAlias(transcript);
  if (!normalized) return false;

  return entries.some((entry) => {
    if (entry.alias === normalized || !entry.alias.startsWith(`${normalized} `)) {
      return false;
    }

    return entry.targets.length !== 1 || entry.targets[0]!.sessionKey !== targetSessionKey;
  });
}

function couldStillBecomeLeadingAlias(transcript: string, entries: VoiceAddressEntry[]): boolean {
  const normalized = normalizeAlias(transcript);
  if (!normalized) return false;

  return entries.some((entry) => entry.alias.startsWith(normalized) && entry.alias !== normalized);
}

function commandToRoute(command: ParsedVoiceCommand): VoiceTurnRoute {
  switch (command.kind) {
    case 'address':
      return {
        kind: 'address',
        targetSessionKey: command.targetSessionKey,
        matchedAlias: command.matchedAlias,
        matchedBy: command.matchedBy,
      };
    case 'ambiguous':
      return {
        kind: 'ambiguous',
        targetSessionKey: command.targetSessionKey,
        matchedAlias: command.matchedAlias,
        candidateSessionKeys: command.candidateSessionKeys,
      };
    case 'none':
      return {
        kind: 'none',
        targetSessionKey: command.targetSessionKey,
      };
  }
}

function withSwitchDecision(route: VoiceTurnRoute, activeSessionKey: string): VoiceTurnSegmentDecision {
  return {
    action: 'switch_only',
    text: '',
    route,
    shouldSwitchSession: route.targetSessionKey !== activeSessionKey,
  };
}

function withEmitDecision(route: VoiceTurnRoute, text: string, activeSessionKey: string): VoiceTurnSegmentDecision {
  return {
    action: 'emit',
    text,
    route,
    shouldSwitchSession: route.targetSessionKey !== activeSessionKey,
  };
}

export function parseVoiceControlCommand(transcript: string): ParsedVoiceControlCommand | null {
  const originalText = transcript.trim();
  if (!originalText) {
    return null;
  }

  const normalizedText = normalizeAlias(originalText);
  const command = VOICE_CONTROL_ALIASES[normalizedText];
  if (!command) {
    return null;
  }

  return {
    command,
    originalText,
    normalizedText,
    matchedPhrase: normalizedText,
  };
}

export function stepPlaybackSpeed(current: number, direction: 'faster' | 'slower'): number {
  if (direction === 'faster') {
    return SPEED_STEPS.find((step) => step > current) ?? SPEED_STEPS[SPEED_STEPS.length - 1]!;
  }

  for (let index = SPEED_STEPS.length - 1; index >= 0; index -= 1) {
    const step = SPEED_STEPS[index]!;
    if (step < current) {
      return step;
    }
  }

  return SPEED_STEPS[0]!;
}

/**
 * Build explicit address targets from current agent/session data.
 *
 * Priority order prefers agent main-session aliases over generic session labels,
 * so saying "personal ..." resolves to `agent:personal:main` when available.
 *
 * Ambiguous same-priority aliases are intentionally omitted from the returned
 * list so callers iterating targets never silently pick one arbitrarily.
 */
export function buildVoiceAddressTargets(params: {
  agents: GatewayAgent[];
  sessions: SessionRow[];
  activeSessionKey?: string;
}): VoiceAddressTarget[] {
  return buildVoiceAddressEntries(params)
    .filter((entry) => entry.targets.length === 1)
    .map((entry) => entry.targets[0]!);
}

/**
 * Parse a transcript for an explicit leading session address.
 *
 * Examples:
 * - "main what's up" -> address main session, send "what's up"
 * - "personal" -> address personal session, switch only, no send
 * - ambiguous aliases return `kind: 'ambiguous'` and do not route
 */
export function parseVoiceCommand(options: ParseVoiceCommandOptions): ParsedVoiceCommand {
  const { transcript, activeSessionKey, agents, sessions } = options;
  const entries = buildVoiceAddressEntries({ agents, sessions, activeSessionKey });
  return parseVoiceCommandWithEntries({ transcript, activeSessionKey, entries });
}

/**
 * Resolve the narrow Voice Lock bypass slice for gate-rejected utterances.
 *
 * Only exact switch-only top-level `main` / `personal` triggers are eligible,
 * plus the curated phonetic aliases already wired into the parser.
 */
export function resolveVoiceLockSwitchOnlyCommand(
  options: ParseVoiceCommandOptions,
): Extract<ParsedVoiceCommand, { kind: 'address' }> | null {
  const command = parseVoiceCommand(options);
  if (command.kind !== 'address' || command.remainingText) {
    return null;
  }

  const expectedAgentId = VOICE_LOCK_SWITCH_ONLY_ALIAS_AGENT_IDS.get(normalizeAlias(command.matchedAlias));
  if (!expectedAgentId) {
    return null;
  }

  if (parseAgentIdFromSessionKey(command.targetSessionKey) !== expectedAgentId) {
    return null;
  }

  return command;
}

/**
 * Route a raw STT segment into either the command router or the arbiter's
 * legacy empty-segment path.
 *
 * Empty STT results intentionally bypass the command controller so the caller
 * can forward them directly to TurnArbiter.addSegment(''), preserving the
 * arbiter's misfire/timeout behavior without disturbing any buffered alias or
 * locked-route state inside the controller.
 */
export function routeVoiceTurnSegment(options: VoiceCommandContext & {
  controller?: VoiceCommandTurnController | null;
  segment: string;
}): RoutedVoiceTurnSegment {
  const { controller, segment, activeSessionKey, agents, sessions } = options;

  if (!segment.trim()) {
    return { kind: 'empty' };
  }

  return {
    kind: 'decision',
    decision: controller?.processSegment({
      segment,
      activeSessionKey,
      agents,
      sessions,
    }) ?? {
      action: 'hold' as const,
      text: '',
      route: { kind: 'none' as const, targetSessionKey: activeSessionKey },
      shouldSwitchSession: false,
    },
  };
}

/**
 * Stateful early-turn router for voice commands.
 *
 * This lets the voice pipeline resolve and lock an addressed session before the
 * first real transcript text is appended into the turn arbiter / protocol path.
 * It also strips the leading address before the turn text is accumulated.
 */
export class VoiceCommandTurnController {
  private bufferedLeadingText = '';
  private lockedRoute: VoiceTurnRoute | null = null;

  reset(): void {
    this.bufferedLeadingText = '';
    this.lockedRoute = null;
  }

  processSegment(options: VoiceCommandContext & { segment: string }): VoiceTurnSegmentDecision {
    const { segment, activeSessionKey, agents, sessions } = options;
    const trimmed = segment.trim();

    if (!trimmed) {
      return {
        action: 'hold',
        text: '',
        route: this.lockedRoute ?? { kind: 'none', targetSessionKey: activeSessionKey },
        shouldSwitchSession: false,
      };
    }

    if (this.lockedRoute) {
      return withEmitDecision(this.lockedRoute, trimmed, activeSessionKey);
    }

    const entries = buildVoiceAddressEntries({ agents, sessions, activeSessionKey });
    const combined = joinTranscriptFragments(this.bufferedLeadingText, trimmed);
    const command = parseVoiceCommandWithEntries({
      transcript: combined,
      activeSessionKey,
      entries,
    });

    if (command.kind === 'address') {
      if (!command.remainingText && hasLongerAliasPrefix(combined, command.targetSessionKey, entries)) {
        this.bufferedLeadingText = combined;
        return {
          action: 'hold',
          text: '',
          route: { kind: 'none', targetSessionKey: activeSessionKey },
          shouldSwitchSession: false,
        };
      }

      const route = commandToRoute(command);
      this.lockedRoute = route;
      this.bufferedLeadingText = '';

      if (!command.remainingText) {
        return withSwitchDecision(route, activeSessionKey);
      }

      return withEmitDecision(route, command.remainingText, activeSessionKey);
    }

    if (command.kind === 'ambiguous') {
      const route = commandToRoute(command);
      this.lockedRoute = route;
      this.bufferedLeadingText = '';
      return withEmitDecision(route, combined, activeSessionKey);
    }

    if (couldStillBecomeLeadingAlias(combined, entries)) {
      this.bufferedLeadingText = combined;
      return {
        action: 'hold',
        text: '',
        route: { kind: 'none', targetSessionKey: activeSessionKey },
        shouldSwitchSession: false,
      };
    }

    const route = commandToRoute(command);
    this.lockedRoute = route;
    this.bufferedLeadingText = '';
    return withEmitDecision(route, combined, activeSessionKey);
  }

  finalizeBufferedCommand(
    context: VoiceCommandContext,
  ): ResolvedVoiceTurnCommand | null {
    if (!this.bufferedLeadingText || this.lockedRoute) {
      return null;
    }

    const command = parseVoiceCommand({
      transcript: this.bufferedLeadingText,
      activeSessionKey: context.activeSessionKey,
      agents: context.agents,
      sessions: context.sessions,
    });
    this.bufferedLeadingText = '';

    return {
      ...commandToRoute(command),
      originalText: command.originalText,
      remainingText: command.remainingText,
      shouldSwitchSession: command.targetSessionKey !== context.activeSessionKey,
    };
  }

  resolveSubmitCommand(options: VoiceCommandContext & { transcript: string }): ResolvedVoiceTurnCommand {
    const { transcript, activeSessionKey, agents, sessions } = options;
    const remainingText = transcript.trim();

    if (this.lockedRoute) {
      return {
        ...this.lockedRoute,
        originalText: remainingText,
        remainingText,
        shouldSwitchSession: this.lockedRoute.targetSessionKey !== activeSessionKey,
      };
    }

    const command = parseVoiceCommand({
      transcript,
      activeSessionKey,
      agents,
      sessions,
    });

    return {
      ...commandToRoute(command),
      originalText: command.originalText,
      remainingText: command.remainingText,
      shouldSwitchSession: command.targetSessionKey !== activeSessionKey,
    };
  }
}
